import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { CreatorSubscriptionPeriod } from '@mlm/shared';
import { CoachButton, Eyebrow, ErrorNote } from './primitives';
import { MethodOption, PlanOption } from './pay-options';
import { fmtPeso, performGatewayHandoff, store } from '../lib/api';
import { coursePaymentMethods } from '../lib/course-access';
import { qk } from '../lib/queries';

export type PurchaseOption = {
  productId: string;
  label: string;
  price: string;
  /** Set for a subscription period; a one-off course has none. */
  period?: CreatorSubscriptionPeriod;
};

const EVERY: Record<CreatorSubscriptionPeriod, string> = {
  monthly: 'every month',
  semi_annual: 'every 6 months',
  annual: 'every year',
};

/**
 * Buying one course, or one subscription period, on the WEB. Never rendered in
 * the phone app (see `courseAction`).
 *
 * A digital purchase is always its own one-line order: that is what lets a
 * card payment for a plan open a renewing subscription (the server only does
 * so for an order that is exactly one house plan), and a course order never
 * waits on a parcel. A THIRD-PARTY creator's item is that creator's own order,
 * which takes a manual method or the cash wallet only.
 */
export function CoursePurchase({
  options,
  seller,
  mode,
}: {
  options: PurchaseOption[];
  seller: { creatorUserId: string | null; isHouse: boolean };
  mode: 'buy' | 'subscribe';
}) {
  const navigate = useNavigate();
  const [optionId, setOptionId] = useState(options[0]?.productId ?? '');
  const [methodCode, setMethodCode] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const isHouse = seller.isHouse || !seller.creatorUserId;

  const ctx = useQuery({
    queryKey: qk.shopContext,
    queryFn: store.paymentMethods,
    staleTime: 5 * 60_000,
  });
  const methods = coursePaymentMethods(ctx.data?.methods ?? [], { isHouse });
  const method = methods.find((m) => m.code === methodCode) ?? null;
  const option = options.find((o) => o.productId === optionId) ?? options[0] ?? null;
  // The server opens a card subscription only for a house plan paid in one go.
  const renews =
    mode === 'subscribe' && isHouse && method?.renewsPlans === true && !!option?.period;

  const pay = useMutation({
    mutationFn: async () => {
      if (!option || !method) throw new Error('Choose how to pay first.');
      const order = isHouse
        ? await store.checkout({
            items: [{ productId: option.productId, qty: 1 }],
            paymentMethodCode: method.code,
          })
        : await store.creatorCheckout({
            creatorUserId: seller.creatorUserId!,
            productId: option.productId,
            paymentMethodCode: method.code,
            buyerSlotId: ctx.data?.slotId ?? null,
          });
      if (method.settlement === 'gateway') {
        performGatewayHandoff(await store.initiateGateway(order.id));
        return null;
      }
      return order.id;
    },
    onSuccess: (orderId) => {
      if (orderId) navigate(`/orders/${orderId}`);
    },
    onError: setError,
  });

  if (!option) return null;

  return (
    <div className="space-y-6">
      {options.length > 1 && (
        <section>
          <Eyebrow className="mb-3">Choose a plan</Eyebrow>
          <div className="space-y-2">
            {options.map((o) => (
              <PlanOption
                key={o.productId}
                plan={o}
                selected={o.productId === option.productId}
                onSelect={() => setOptionId(o.productId)}
              />
            ))}
          </div>
        </section>
      )}
      <section>
        <Eyebrow className="mb-3">How would you like to pay?</Eyebrow>
        {ctx.isPending ? (
          <p className="eyebrow">Loading…</p>
        ) : methods.length === 0 ? (
          <p className="text-[0.93rem] text-ink/70">
            No payment method is available for this right now. Please check back soon.
          </p>
        ) : (
          <div className="space-y-2">
            {methods.map((m) => (
              <MethodOption
                key={m.code}
                method={m}
                selected={m.code === method?.code}
                onSelect={() => setMethodCode(m.code)}
              />
            ))}
          </div>
        )}
        {renews && option.period && (
          <p className="mt-3 border border-[rgba(176,141,63,0.4)] px-4 py-3 text-[0.9rem] leading-relaxed text-ink/85">
            Paying by card starts a subscription: {fmtPeso(option.price)} {EVERY[option.period]}{' '}
            until you cancel. You can cancel anytime from Learn.
          </p>
        )}
      </section>
      {error !== null && <ErrorNote error={error} />}
      <CoachButton
        kind="gold"
        block
        disabled={!method || pay.isPending}
        onClick={() => {
          setError(null);
          pay.mutate();
        }}
      >
        {pay.isPending
          ? 'Starting…'
          : mode === 'subscribe'
            ? `Subscribe, ${fmtPeso(option.price)}`
            : `Pay ${fmtPeso(option.price)}`}
      </CoachButton>
    </div>
  );
}
